import "@pentacode/app/src/elements/entity-filters";
import "@pentacode/app/src/elements/popover";
import "@pentacode/app/src/elements/scroller";
import { shared } from "@pentacode/app/src/styles";
import { dateAdd, formatDateShort } from "@pentacode/core";
import type { PentacodeAPIModels } from "@pentacode/rest";
import "@pentacode/ui/components";
import { css, html, LitElement } from "lit";
import { customElement, property, query } from "lit/decorators.js";

@customElement("ptc-note")
export class Note extends LitElement {
    @property({ attribute: false })
    note: PentacodeAPIModels["Note"];

    @property({ type: Boolean })
    collapsed = true;

    @property({ type: Number })
    maxHeight = 50;

    @query("pre")
    private _noteTextContainer: HTMLPreElement;

    @query(".box")
    private _box: HTMLDivElement;

    @query(".expand-button")
    private _expandButton: HTMLDivElement;

    private get _expandable() {
        return this._noteTextContainer && this._noteTextContainer.offsetHeight > this.maxHeight;
    }

    private _mutationObserver = new MutationObserver(() => this._updateDynamicStyles());
    private _intersectionObserver = new IntersectionObserver(() => this._updateDynamicStyles());

    private async _updateDynamicStyles() {
        if (!this._expandButton) {
            await this.updateComplete;
        }
        this._expandButton.style.display = this._expandable ? "" : "none";
        this._box.style.paddingBottom = this._expandable && !this.collapsed ? "2em" : "";
    }

    connectedCallback() {
        super.connectedCallback();
        this._mutationObserver.observe(this, { childList: true, subtree: true });
        this._intersectionObserver.observe(this);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._mutationObserver.disconnect();
        this._intersectionObserver.disconnect();
    }

    updated() {
        void this._updateDynamicStyles();
    }

    static styles = [
        shared,
        css`
            :host {
                display: block;
            }
        `,
    ];

    render() {
        const note = this.note;
        if (!note) {
            return;
        }
        return html`
            <div
                class="padded box relative"
                style="--color-highlight: ${note.color || "unset"}; transition: padding-bottom 0.2s;"
            >
                <div class="subtle spacing horizontal layout" style="margin-bottom: 0.25em">
                    <div class="stretch">
                        ${formatDateShort(note.start)}
                        ${note.end !== dateAdd(note.start, { days: 1 }) ? `- ${formatDateShort(note.end)}` : ""}
                    </div>
                </div>

                <ptc-ui-collapse .collapsedHeight=${this.maxHeight} .collapsed=${this.collapsed}>
                    <pre style="white-space: pre-wrap; line-height: 1.3em;">${note.text}</pre>
                </ptc-ui-collapse>

                <div class="bottom right expand-button absolute" style="background: rgba(255, 255, 255, 0.9);">
                    ${this.collapsed
                        ? html`<div class="padded link" @click=${() => (this.collapsed = false)}>
                              mehr <ptc-ui-icon name="icon.toggle.expand-note" class="smaller"></ptc-ui-icon>
                          </div>`
                        : html` <div class="padded link" @click=${() => (this.collapsed = true)}>
                              <ptc-ui-icon name="icon.toggle.collapse-note" class="smaller"></ptc-ui-icon> weniger
                          </div>`}
                </div>
            </div>
        `;
    }
}
